import {useCallback, useMemo} from 'react'
import {useMutation, useQuery, useQueryClient} from 'react-query'

import {
  DeliveryAnnotation,
  CreateDeliveryAnnotationRequest,
  UpdateDeliveryAnnotationRequest,
  QueryDeliveryAnnotationsParams,
  createDeliveryAnnotation,
  queryDeliveryAnnotations,
  updateDeliveryAnnotation,
  deleteDeliveryAnnotation
} from '@hconnect/apiclient'

import {api} from '../../../../App.global'

// Query keys
const DELIVERY_ANNOTATIONS_KEY = 'deliveryAnnotations'

type UseDeliveryAnnotationsParams = {
  /** The order ID — enables the order-level query that aggregates all delivery annotations */
  orderId?: string
  /** The delivery ID — enables the delivery-level query + CRUD actions */
  deliveryId?: string
  /** Master toggle (e.g. permission check). Default true */
  enabled?: boolean
}

/**
 * Unified hook for delivery-annotation operations scoped to an order / delivery.
 *
 * - Provide only `orderId` for the order-level summary (which deliveries have annotations).
 * - Provide `orderId` + `deliveryId` when you also need the single-delivery
 *   annotation and its CRUD actions.
 *
 * All queries share the `deliveryAnnotations` key prefix so every mutation
 * automatically invalidates both query levels.
 */
export const useDeliveryAnnotations = ({
  orderId,
  deliveryId,
  enabled = true
}: UseDeliveryAnnotationsParams) => {
  const queryClient = useQueryClient()

  // ── Order-level query ──────────────────────────────────────────────
  const orderQueryParams: QueryDeliveryAnnotationsParams = {
    entityType: 'Delivery',
    parentEntityType: 'Order',
    parentEntityId: orderId!
  }

  const {
    data: annotations = [],
    isLoading,
    isError,
    refetch
  } = useQuery<DeliveryAnnotation[], Error>(
    [DELIVERY_ANNOTATIONS_KEY, 'orderDeliveries', orderId],
    async () => queryDeliveryAnnotations(api)(orderQueryParams),
    {enabled: enabled && !!orderId}
  )

  const deliveryIdsWithDeliveryAnnotation = useMemo(
    () => new Set(annotations.map((n) => n.entityId)),
    [annotations]
  )

  // ── Delivery-level query ───────────────────────────────────────────
  const deliveryQueryParams: QueryDeliveryAnnotationsParams = {
    entityType: 'Delivery',
    entityId: deliveryId!,
    ...(orderId && {parentEntityId: orderId})
  }

  const {data: deliveryAnnotations, isLoading: isLoadingDeliveryAnnotation, isError: isErrorDeliveryAnnotation} = useQuery<
    DeliveryAnnotation[],
    Error
  >(
    [DELIVERY_ANNOTATIONS_KEY, deliveryQueryParams],
    async () => queryDeliveryAnnotations(api)(deliveryQueryParams),
    {enabled: enabled && !!deliveryId}
  )

  // API allows only one annotation per entity
  const deliveryAnnotation = deliveryAnnotations?.[0]
  const hasDeliveryAnnotation = !!deliveryAnnotation

  // ── Mutations ──────────────────────────────────────────────────────
  const invalidateAll = () => queryClient.invalidateQueries(DELIVERY_ANNOTATIONS_KEY)

  const {mutateAsync: createDeliveryAnnotationMutation, isLoading: isCreating} = useMutation<
    DeliveryAnnotation,
    Error,
    CreateDeliveryAnnotationRequest
  >(async (request) => createDeliveryAnnotation(api)(request), {onSuccess: invalidateAll})

  const {mutateAsync: updateDeliveryAnnotationMutation, isLoading: isUpdating} = useMutation<
    DeliveryAnnotation,
    Error,
    {deliveryAnnotationId: string; request: UpdateDeliveryAnnotationRequest}
  >(async ({deliveryAnnotationId, request}) => updateDeliveryAnnotation(api)(deliveryAnnotationId, request), {
    onSuccess: invalidateAll
  })

  const {mutateAsync: deleteDeliveryAnnotationMutation, isLoading: isDeleting} = useMutation<
    void,
    Error,
    {deliveryAnnotationId: string}
  >(
    async ({deliveryAnnotationId}) => {
      await deleteDeliveryAnnotation(api)(deliveryAnnotationId)
    },
    {onSuccess: invalidateAll}
  )

  // ── CRUD action helpers (delivery-scoped) ──────────────────────────
  const handleAddDeliveryAnnotation = useCallback(
    async (content: string, dataScope?: Pick<CreateDeliveryAnnotationRequest, 'customerId' | 'orgUnitId' | 'countryId' | 'businessLine' | 'siteId'>) => {
      if (!deliveryId) throw new Error('deliveryId is required to add an annotation')
      const request: CreateDeliveryAnnotationRequest = {
        entityType: 'Delivery',
        entityId: deliveryId,
        content,
        ...(orderId && {
          parentEntityType: 'Order',
          parentEntityId: orderId
        }),
        ...dataScope
      } as CreateDeliveryAnnotationRequest
      await createDeliveryAnnotationMutation(request)
    },
    [deliveryId, orderId, createDeliveryAnnotationMutation]
  )

  const handleEditDeliveryAnnotation = useCallback(
    async (content: string) => {
      if (!deliveryAnnotation?.id) throw new Error('No existing annotation to edit')
      await updateDeliveryAnnotationMutation({deliveryAnnotationId: deliveryAnnotation.id, request: {content}})
    },
    [deliveryAnnotation, updateDeliveryAnnotationMutation]
  )

  const handleDeleteDeliveryAnnotation = useCallback(async () => {
    if (!deliveryAnnotation?.id) throw new Error('No existing annotation to delete')
    await deleteDeliveryAnnotationMutation({deliveryAnnotationId: deliveryAnnotation.id})
  }, [deliveryAnnotation, deleteDeliveryAnnotationMutation])

  return {
    // Order-level
    annotations: annotations,
    deliveryIdsWithAnnotations: deliveryIdsWithDeliveryAnnotation,
    deliveriesWithDeliveryAnnotationsCount: deliveryIdsWithDeliveryAnnotation.size,
    isLoading,
    isError,
    refetch,
    // Delivery-level
    deliveryAnnotation: deliveryAnnotation,
    hasDeliveryAnnotation: hasDeliveryAnnotation,
    isLoadingDeliveryAnnotation: isLoadingDeliveryAnnotation,
    isErrorDeliveryAnnotation: isErrorDeliveryAnnotation,
    // CRUD actions
    handleAddDeliveryAnnotation,
    handleEditDeliveryAnnotation,
    handleDeleteDeliveryAnnotation,
    isSaving: isCreating || isUpdating,
    isDeleting
  }
}
